/**
 * Static, curated demo fixtures for the Upstream Master Lot Builder mock-up.
 * Shapes mirror the intended live persistence so mock services can be swapped
 * for real endpoints without touching components.
 *
 * Intended schema mirror:
 *   - REFINING_PARTNERS  -> `refining_partners` table (FK to companies)
 *   - UPSTREAM_REFINING_CONTRACTS -> `upstream_refining_contracts` table
 *     (FK to refining_partners)
 *   - `expectedMilestones` on each contract  -> `expected_milestones` JSONB
 *     column on `upstream_refining_contracts` (array of `{kind, label}`,
 *     validated against the `master_lot_milestones.kind` enum at write time).
 *     Holding it in JSONB rather than a join table keeps the schema honest:
 *     we don't know enough yet to over-architect per-contract milestone shape,
 *     and the contract version chain (parent_contract_id) already gives us
 *     immutability for free. Revisit if a third partner introduces structural
 *     milestone metadata (e.g. SLA targets per kind).
 *
 * Source lots are NOT mocked here — they come from the live
 * `/refinery/upstream-source-lots` endpoint (admin-only, returns settled
 * refinery lots pre-decomposed into bar/house_pin/customer_pin units).
 *
 * THIS FILE IS NOT WIRED TO PRODUCTION DATA. Partner / contract numbers are
 * believable but fictional and exist only to make demos concrete.
 */

export const REFINING_PARTNERS = [
  {
    id: 'partner-asahi',
    code: 'ASAHI',
    displayName: 'Asahi Refining',
    opsNotes:
      'Ops contact on file (operator-side directory). ' +
      'Dual-legal-entity / single-physical-facility billing: gold-bearing material (' +
      'contract `contract-asahi-au-2026`, quote SR2604523) is contracted via Asahi ' +
      'Refining USA, Inc. (Salt Lake City facility); silver-bearing material (' +
      '`contract-asahi-ag-2026`, quote SR2605539) is contracted via Asahi Refining ' +
      'Canada Ltd. (Brampton billing entity) for legal/billing reasons even though ' +
      'physical refining for BOTH contracts happens at the SLC facility. House pin ' +
      'paid by partner; customer pin returned and held back from net weight. ' +
      'Partner-issued assay results are headline-only (no fire/XRF chain).',
  },
  {
    id: 'partner-igr',
    code: 'IGR',
    displayName: 'IGR (International Gold Refinery)',
    opsNotes:
      'Logistics-driven minimum (~1 metric ton); lot accumulation expected. House pin only.',
  },
  {
    id: 'partner-metalor',
    code: 'METALOR',
    displayName: 'Metalor USA (North Attleboro)',
    opsNotes:
      'Karat Medium Scrap program. Purity-content tiering; Pt/Pd assay opt-in at sales order.',
  },
  {
    id: 'partner-saxonia',
    code: 'SAXONIA',
    displayName: 'Saxonia Edelmetalle (Halsbrücke, Germany)',
    opsNotes:
      'EUR-denominated refining schedule. Partner issues dual-language statements ' +
      '(EUR primary + USD-converted English secondary). Operators transcribe the ' +
      'USD-converted refining charge as an adHocFee at settlement. SLA anchor on ' +
      '"after written release" — modeled via the existing `received` milestone.',
  },
]

/**
 * `metalRates` is the upstream contract API field. The customer contract
 * column is still `refinery_contracts.metal_rates`, with the same row shape:
 * `[{metalType, tierMin, tierMax, accountabilityPercent, fee}]`.
 *
 *   - `accountabilityPercent` — fraction of fine metal the partner credits us.
 *   - `fee` — USD per credited fine-toz (NOT a retention percent). Mirrors
 *     customer-side `fee × settlementWeight = USD refining fee`. Used by
 *     `computeSettlementPreview` to derive each metal's `refiningFeeUsd` on
 *     settlement entry.
 *
 * `assayFee` carries per-metal assay charges `{feeUsd, optInAtSalesOrder?}`.
 * `standardFees.treatmentFeeUsd` is the lot-level treatment charge (until
 * the otherFees slice generalizes it).
 *
 * Legacy note: older drafts used flat `standardFees.assayFeeUsd`; the editor
 * and validators fan that out evenly across `paidMetals` on load.
 *
 * `standardFees` also historically carried treatment as:
 * `treatmentFeeUsd` (upstream-specific analog of customer-side
 * `incoming_treatment_fee_per_unit` — upstream is the one running the
 * treatment, so we pay flat). One-off charges that don't fit these slots go
 * into `settlement.adHocFees` at entry time.
 *
 * Realistic shapes: Asahi Au is flat (one tier); IGR is tiered so larger
 * lots earn higher accountability and a lower per-toz refining fee.
 */
export const UPSTREAM_REFINING_CONTRACTS = [
  // Real Asahi Refining USA gold-scrap-containing-PGM contract per
  // Commercial Term Sheet SR2604523 (2026-04-30; supersedes SR2602493's
  // 10bd/15bd Au-SLA split with a flat 10bd across all gold tiers).
  // Models cross-metal Au qualifier on Ag/Pt/Pd accountability, mixed
  // feeBasis (creditedFineToz on Au, netWeightReceivedToz on Ag/Pt/Pd),
  // and per-metal payable/deduction thresholds.
  {
    id: 'contract-asahi-au-2026',
    refiningPartnerId: 'partner-asahi',
    title: 'Asahi Au+PGM — 2026 terms (SR2604523)',
    effectiveFrom: '2026-04-30',
    effectiveTo: '2099-12-31',
    currency: 'USD',
    materialAcceptanceCriteria:
      'Gold scrap containing PGM. Approx 1000 ozt of material per lot ' +
      '(sizing guidance, not a hard floor). Final settlement anchored on ' +
      'day-after-receipt at SLC or BTN refineries.',
    tierDimension: 'purityPct',
    tierQualifyingMetal: 'au',
    paidMetals: ['au', 'ag', 'pt', 'pd'],
    primaryMetals: ['au'],
    lotWeightRequirements: [
      {kind: 'guidance', value: 1000, unit: 'ozt', basis: 'material_per_lot', enforcement: 'advisory'},
    ],
    settlementTerms:
      'Asahi Refining USA, Inc., 4601 2100 S, Salt Lake City UT 84120 ' +
      '(contract counterparty / billing entity / refining facility). ' +
      'F.O.B. SLC refinery. US funds. All other terms per the Asahi ' +
      'Refining Standard Agreement. Quote SR2604523. Receipt anchor may ' +
      'fire at SLC or BTN refineries.',
    metalRates: [
      // Half-open [tierMin, tierMax) under tierQualifyingMetal: 'au'; tierMin/Max in plain percent. Au own-qualified at flat 10bd SLA per SR2604523. Ag/Pt/Pd cross-qualified on Au; refining billed against lot's reported gross via netWeightReceivedToz (Pt at $25.00/ozt net wt received).
      {metalType: 'au', tierMin: 99.5, tierMax: null, accountabilityPercent: 100.00, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 10, notes: 'Au ≥ 99.5% — full credit per SR2604523'},
      {metalType: 'au', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 99.80, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 10, notes: null},
      // Ag/Pt/Pd cross-qualified on Au — only the `[50.0, 99.5)` band is
      // populated because below 50% Au the lot ships as Ag-bearing
      // material under the silver contract instead.
      {metalType: 'ag', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 95.00, fee: 0.25,  feeBasis: 'netWeightReceivedToz', settlementBusinessDays: 10, notes: 'Ag refining priced against lot net wt received'},
      {metalType: 'pt', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 90.00, fee: 25.00, feeBasis: 'netWeightReceivedToz', settlementBusinessDays: 60, notes: 'Pt refining priced against lot net wt received'},
      {metalType: 'pd', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 90.00, fee: 25.00, feeBasis: 'netWeightReceivedToz', settlementBusinessDays: 60, notes: 'Pd refining priced against lot net wt received'},
    ],
    referenceFeeSchedule: {
      perLotMinimumFee: 400,
      metalThresholds: {
        ag: {minimumPayablePct: 0.50, minimumDeductionPct: 0.20},
        pt: {minimumPayablePct: 0.05, minimumDeductionPct: 0.03},
        pd: {minimumPayablePct: 0.05, minimumDeductionPct: 0.03},
      },
      additionalNotes: null,
    },
    slaModifiers: null,
    assayFee: {
      au: {
        feeUsd: 100,
        tierQualifyingMetal: 'au',
        purityTiers: [
          {minPct: 0, maxPct: 99.5, feeUsd: 100},
          {minPct: 99.5, maxPct: null, feeUsd: 75},
        ],
      },
    },
    otherFees: [
      {code: 'treatment', kind: 'perToz', perTozUsd: 0.70, notes: 'Single Au band 500+ — no purityTiers'},
      {code: 'wire', kind: 'flat', flatUsd: 25, notes: null},
    ],
    standardFees: {},
    expectedMilestones: [
      {kind: 'shipped',      label: 'Shipped'},
      {kind: 'received',     label: 'Acknowledged Receipt'},
      {kind: 'melted',       label: 'Melt Date'},
      {kind: 'pin_returned', label: 'Customer Pin Returned'},
      {kind: 'assayed',      label: 'Assay Result Provided'},
      {kind: 'settled',      label: 'Settlement'},
    ],
  },
  // Real Asahi Refining Canada silver-bullion contract per SR2605539 (2026-05-05). Asahi Canada is contract counterparty / billing entity; physical refining at the SLC facility per the dual-entity / single-facility arrangement documented on the partner record.
  {
    id: 'contract-asahi-ag-2026',
    refiningPartnerId: 'partner-asahi',
    title: 'Asahi Ag bullion — 2026 terms (SR2605539)',
    effectiveFrom: '2026-05-05',
    effectiveTo: '2099-12-31',
    currency: 'USD',
    materialAcceptanceCriteria:
      'Directly meltable silver bullion >88% Ag fineness. See impurity ' +
      'charge table for deleterious-element limits — bands marked "rejected" ' +
      'will be refused at the dock; bands marked "to_review" require partner ' +
      'pre-approval.',
    tierDimension: 'purityPct',
    tierQualifyingMetal: 'ag',
    paidMetals: ['ag', 'au'],
    primaryMetals: ['ag'],
    lotWeightRequirements: [
      {kind: 'minimum', value: 10000, unit: 'ozt', basis: 'material_per_lot', enforcement: 'hard'},
    ],
    settlementTerms:
      'Asahi Refining Canada Ltd., 130 Glidden Road, Brampton ON L6W 3M8 ' +
      '(contract counterparty / billing entity). F.O.B. SLC refinery — ' +
      'physical refining performed at Asahi Refining USA Salt Lake City ' +
      'facility under arrangement with Asahi Canada. US funds. All other ' +
      'terms per the Asahi Refining Standard Agreement. Quote SR2605539.',
    metalRates: [
      // Half-open [tierMin, tierMax) under tierQualifyingMetal: 'ag'; plain percent. Three Ag bands ladder credit on assay; Au row cross-qualified on Ag fineness ($1.55/credited-fine-toz, NOT waived — the only Asahi-silver row exercising credited-fine-toz refining math).
      {metalType: 'ag', tierMin: 99.9, tierMax: null, accountabilityPercent: 99.75, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 30, notes: 'Highest band — fine silver'},
      {metalType: 'ag', tierMin: 90.0, tierMax: 99.9, accountabilityPercent: 98.75, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 45, notes: null},
      {metalType: 'ag', tierMin: 88.0, tierMax: 90.0, accountabilityPercent: 98.50, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 45, notes: 'Lowest accepted Ag band per material acceptance criteria'},
      // Au cross-qualified on Ag fineness — only the `[0, 99.9)` band
      // (matches any Ag below the fine-silver tier). At Ag ≥ 99.9, the
      // Au row does NOT match (lots with Au content + fine-silver Ag
      // are not anticipated under the silver-bullion contract).
      {metalType: 'au', tierMin: 0, tierMax: 99.9, accountabilityPercent: 98.00, fee: 1.55, feeBasis: 'creditedFineToz', settlementBusinessDays: 45, notes: 'Au cross-qualified on Ag fineness; $1.55/credited-fine-ozt refining'},
    ],
    referenceFeeSchedule: {
      perLotMinimumFee: 1000,
      // 11 deleterious elements per SR2605539 — Sb/Bi/Cu/Hg/Te/Cd/Sn/As/Pb/Se
      // with 3–5 PPM bands at perKgNetWeightReceived basis; Be with a
      // single state: 'rejected' band (no PPM threshold — Cannot Process).
      impurityCharges: [
        {element: 'sb', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 2000, state: 'charge', ratePerKg: 2.00},
          {maxPpm: 10000, state: 'charge', ratePerKg: 3.00},
          {maxPpm: 20000, state: 'charge', ratePerKg: 4.00},
          {maxPpm: null, state: 'rejected'},
        ]},
        {element: 'bi', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 100,  state: 'free'},
          {maxPpm: 200,  state: 'charge', ratePerKg: 2.00},
          {maxPpm: 1000, state: 'charge', ratePerKg: 3.00},
          {maxPpm: 2000, state: 'charge', ratePerKg: 4.00},
          {maxPpm: null, state: 'rejected'},
        ]},
        {element: 'cu', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 100000, state: 'free'},
          {maxPpm: 150000, state: 'charge', ratePerKg: 6.50},
          {maxPpm: 200000, state: 'charge', ratePerKg: 9.60},
          {maxPpm: null, state: 'rejected'},
        ]},
        {element: 'hg', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 100, state: 'free'},
          {maxPpm: 500, state: 'charge', ratePerKg: 3.00},
          {maxPpm: 750, state: 'charge', ratePerKg: 4.00},
          {maxPpm: null, state: 'rejected'},
        ]},
        {element: 'te', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 50, state: 'free'},
          {maxPpm: 500, state: 'charge', ratePerKg: 2.00},
          {maxPpm: null, state: 'to_review'},
        ]},
        {element: 'cd', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 2500, state: 'free'},
          {maxPpm: 300000, state: 'charge', ratePerKg: 3.00},
          {maxPpm: null, state: 'to_review'},
        ]},
        {element: 'sn', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 2000, state: 'free'},
          {maxPpm: 10000, state: 'charge', ratePerKg: 2.00},
          {maxPpm: 30000, state: 'charge', ratePerKg: 4.00},
          {maxPpm: null, state: 'to_review'},
        ]},
        {element: 'as', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 2000, state: 'free'},
          {maxPpm: 10000, state: 'charge', ratePerKg: 2.00},
          {maxPpm: 20000, state: 'charge', ratePerKg: 3.00},
          {maxPpm: null, state: 'to_review'},
        ]},
        {element: 'pb', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 2000, state: 'free'},
          {maxPpm: 5000, state: 'charge', ratePerKg: 0.80},
          {maxPpm: 20000, state: 'charge', ratePerKg: 2.30},
          {maxPpm: 50000, state: 'charge', ratePerKg: 3.50},
          {maxPpm: null, state: 'to_review'},
        ]},
        {element: 'se', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: 5000, state: 'free'},
          {maxPpm: 20000, state: 'charge', ratePerKg: 1.50},
          {maxPpm: 30000, state: 'charge', ratePerKg: 2.50},
          {maxPpm: null, state: 'to_review'},
        ]},
        // Be — no PPM threshold, partner rejects outright per SR2605539.
        {element: 'be', basis: 'perKgNetWeightReceived', bands: [
          {maxPpm: null, state: 'rejected'},
        ]},
      ],
      metalThresholds: {
        au: {minimumPayablePct: 0.05, minimumDeductionPct: 0.03},
      },
      additionalNotes: 'Per the Asahi Refining Standard Agreement.',
    },
    slaModifiers: [
      {
        when: {lotWeightKgMax: 200},
        addBusinessDays: 10,
        notes: 'Lots under 200 kg add +10 business days to the per-tier SLA.',
      },
      {
        when: {hasImpurityCharges: true},
        addBusinessDays: 10,
        notes: 'Lots with any partner-billed impurity charge add +10 business days.',
      },
    ],
    assayFee: {
      ag: {feeUsd: 100},
    },
    otherFees: [
      // Purity-tiered treatment fee: three half-open bands matching the Ag accountability ladder. perTozUsd applies against credited fine-toz at settlement. minPct/maxPct in plain percent (resolveFeeAmount compares against ctx.qualifyingPurityPct).
      {code: 'treatment', kind: 'perToz', perTozUsd: 0.50, purityTiers: [
        {minPct: 88,   maxPct: 90,   perTozUsd: 0.50},
        {minPct: 90,   maxPct: 99.9, perTozUsd: 0.50},
        {minPct: 99.9, maxPct: null, perTozUsd: 0.35},
      ], notes: 'Three Ag-fineness bands per SR2605539 treatment schedule'},
    ],
    standardFees: {},
    expectedMilestones: [
      {kind: 'shipped',  label: 'Shipped'},
      {kind: 'received', label: 'Acknowledged Receipt'},
      {kind: 'melted',   label: 'Melt Date'},
      {kind: 'assayed',  label: 'Assay Result Provided'},
      {kind: 'settled',  label: 'Settlement'},
    ],
  },
  {
    id: 'contract-igr-ag-2026',
    refiningPartnerId: 'partner-igr',
    title: 'IGR Ag/Au — 2026 terms',
    effectiveFrom: null,
    effectiveTo: '2099-12-31',
    currency: 'USD',
    materialAcceptanceCriteria: null,
    tierDimension: 'weightToz',
    paidMetals: ['ag', 'au'],
    primaryMetals: ['ag'],
    lotWeightRequirements: [
      {kind: 'minimum', value: 1, unit: 'metric_tonne', basis: 'shipment', enforcement: 'hard'},
    ],
    settlementTerms:
      'Logistics-driven minimum (~1 metric ton). House pin only. Settlement T+15 from receipt.',
    metalRates: [
      // Half-open [tierMin, tierMax): [0, 20000) at 95% / $0.05; [20000, null] at 98% / $0.03. feeBasis stamped explicitly on every tier row.
      {metalType: 'ag', tierMin: 0,     tierMax: 20000, accountabilityPercent: 95, fee: 0.05, feeBasis: 'creditedFineToz', settlementBusinessDays: 15, notes: null},
      {metalType: 'ag', tierMin: 20000, tierMax: null,  accountabilityPercent: 98, fee: 0.03, feeBasis: 'creditedFineToz', settlementBusinessDays: 15, notes: null},
      // Au — flat: incidental Au content from Ag-dominant lots, refined at a per-toz penalty.
      {metalType: 'au', tierMin: 0, tierMax: null, accountabilityPercent: 95, fee: 2.50, feeBasis: 'creditedFineToz', settlementBusinessDays: 15, notes: null},
    ],
    referenceFeeSchedule: null,
    slaModifiers: null,
    assayFee: {
      ag: {feeUsd: 175.00},
      au: {feeUsd: 175.00},
    },
    otherFees: [
      {code: 'treatment', kind: 'flat', flatUsd: 150, notes: null},
    ],
    standardFees: {},
    expectedMilestones: [
      { kind: 'shipped',  label: 'Shipped' },
      { kind: 'received', label: 'Acknowledged Receipt' },
      { kind: 'melted',   label: 'Melt Date' },
      { kind: 'assayed',  label: 'Assay Result Provided' },
      { kind: 'settled',  label: 'Settlement' },
    ],
  },
  {
    id: 'contract-metalor-karat-medium-scrap-2026',
    refiningPartnerId: 'partner-metalor',
    title: 'Metalor Karat Medium Scrap — 2026 terms',
    currency: 'USD',
    materialAcceptanceCriteria: null,
    tierDimension: 'purityPct',
    paidMetals: ['au', 'ag', 'pt', 'pd'],
    primaryMetals: ['au', 'ag'],
    lotWeightRequirements: [
      {kind: 'minimum', value: 200, unit: 'ozt', basis: 'shipment', enforcement: 'hard'},
    ],
    effectiveFrom: null,
    effectiveTo: '2099-12-31',
    settlementTerms:
      'Karat Medium Scrap (USA origin). Settlement SLA per metal from receipt. ' +
      'Pt/Pd assay charged only when opted in at settlement.',
    metalRates: [
      {
        metalType: 'au',
        tierMin: 40,
        tierMax: null,
        accountabilityPercent: 99.75,
        fee: 0,
        feeWaived: true,
        feeBasis: 'creditedFineToz',
        minimumReturnToz: 0.3,
        settlementBusinessDays: 10,
        notes: null,
      },
      {
        metalType: 'ag',
        tierMin: 1,
        tierMax: null,
        accountabilityPercent: 97,
        fee: 0,
        feeWaived: true,
        feeBasis: 'creditedFineToz',
        minimumReturnToz: 5.0,
        settlementBusinessDays: 10,
        notes: null,
      },
      {
        metalType: 'pt',
        tierMin: 0.1,
        tierMax: null,
        accountabilityPercent: 85,
        fee: 15,
        feeBasis: 'creditedFineToz',
        minimumReturnToz: 0.3,
        settlementBusinessDays: 20,
        notes: null,
      },
      {
        metalType: 'pd',
        tierMin: 0.1,
        tierMax: null,
        accountabilityPercent: 85,
        fee: 15,
        feeBasis: 'creditedFineToz',
        minimumReturnToz: 0.3,
        settlementBusinessDays: 30,
        notes: null,
      },
    ],
    referenceFeeSchedule: null,
    slaModifiers: null,
    assayFee: {
      au: {feeUsd: 75},
      ag: {feeUsd: 75},
      pt: {feeUsd: 60, optInAtSalesOrder: true},
      pd: {feeUsd: 60, optInAtSalesOrder: true},
    },
    otherFees: [
      {code: 'treatment', kind: 'perToz', perTozUsd: 0.75, minimumUsd: 300, notes: null},
      {code: 'copper_addition', kind: 'perToz', perTozUsd: 0.5, minimumUsd: 50, conditional: true, notes: null},
    ],
    standardFees: {},
    expectedMilestones: [
      {kind: 'shipped', label: 'Shipped'},
      {kind: 'received', label: 'Acknowledged Receipt'},
      {kind: 'melted', label: 'Melt Date'},
      {kind: 'assayed', label: 'Assay Result Provided'},
      {kind: 'settled', label: 'Settlement'},
    ],
  },
  // Saxonia Edelmetalle bullion contract. Exercises EUR currency, multi-section referenceFeeSchedule (refiningCosts + single-band perKgElement impurity + additionalNotes), cross-metal Ag tier qualifier, six Ag accountability bands (half-open), and slaModifiers (Ag<70% +10bd). All refining-fee dollars live in the EUR-priced referenceFeeSchedule; metalRates rows are feeWaived. Operators transcribe partner's USD-converted refining charge at settlement as an adHocFees row (€/kg-credited-metal basis).
  {
    id: 'contract-saxonia-bullion-2026',
    refiningPartnerId: 'partner-saxonia',
    title: 'Saxonia bullion — 2026 terms (term sheet 11/03/26-065/54-1)',
    effectiveFrom: '2026-03-11',
    effectiveTo: '2099-12-31',
    currency: 'EUR',
    materialAcceptanceCriteria:
      'Bullion / dore / sweeps eligible. Material > 70% Ag fineness settles ' +
      'on the standard SLA; sub-70% material extends the SLA by 10 business ' +
      'days per the slaModifier below. This fixture anchors SLA on the ' +
      '`received` milestone; Saxonia\'s live terms use "after written release" ' +
      '(no separate release milestone in the current schema).',
    tierDimension: 'purityPct',
    tierQualifyingMetal: 'ag',
    paidMetals: ['au', 'ag', 'pt', 'pd'],
    primaryMetals: ['ag'],
    lotWeightRequirements: [
      {kind: 'maximum', value: 2, unit: 'metric_tonne', basis: 'settlement_lot', enforcement: 'hard'},
      {kind: 'maximum', value: 3, unit: 'metric_tonne', basis: 'delivery', enforcement: 'hard'},
    ],
    settlementTerms:
      'Saxonia Edelmetalle GmbH, Halsbrücke (Germany). Dual-language statement ' +
      '(EUR primary; English/USD secondary). Refining schedule in `referenceFeeSchedule.refiningCosts` ' +
      'is partner-billed in EUR per kg credited metal — operators transcribe ' +
      'the USD-converted refining charge as an adHocFees row at settlement.',
    metalRates: [
      {metalType: 'ag', tierMin: 99, tierMax: null, accountabilityPercent: 99.9, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 25, notes: null},
      {metalType: 'ag', tierMin: 90, tierMax: 99, accountabilityPercent: 99.5, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 25, notes: null},
      {metalType: 'ag', tierMin: 80, tierMax: 90, accountabilityPercent: 99.2, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 25, notes: null},
      {metalType: 'ag', tierMin: 60, tierMax: 80, accountabilityPercent: 99.0, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 25, notes: null},
      {metalType: 'ag', tierMin: 40, tierMax: 60, accountabilityPercent: 98.5, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 35, notes: null},
      {metalType: 'ag', tierMin: 0, tierMax: 40, accountabilityPercent: 98.0, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 35, notes: null},
      {metalType: 'au', tierQualifyingMetal: 'au', tierMin: 0.1, tierMax: null, accountabilityPercent: 98.8, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 25, notes: null},
      {metalType: 'au', tierQualifyingMetal: 'au', tierMin: 0, tierMax: 0.1, accountabilityPercent: 98.0, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 25, notes: 'Minimum deduction 30 g/t'},
      {metalType: 'pt', tierQualifyingMetal: 'pt', tierMin: 0.1, tierMax: null, accountabilityPercent: 98.0, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 40, notes: null},
      {metalType: 'pt', tierQualifyingMetal: 'pt', tierMin: 0, tierMax: 0.1, accountabilityPercent: 97.0, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 40, notes: 'Minimum deduction 30 g/t'},
      {metalType: 'pd', tierQualifyingMetal: 'pd', tierMin: 0.1, tierMax: null, accountabilityPercent: 98.0, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 40, notes: null},
      {metalType: 'pd', tierQualifyingMetal: 'pd', tierMin: 0, tierMax: 0.1, accountabilityPercent: 97.0, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 40, notes: 'Minimum deduction 30 g/t'},
    ],
    referenceFeeSchedule: {
      // Refining costs partner-billed in EUR per kg of credited metal. Never enters compute — operators transcribe USD-converted refining charge as adHocFees at settlement.
      refiningCosts: {
        au: {ratePerKg: 130,  basis: 'perKgCreditedMetal', currency: 'EUR'},
        ag: {ratePerKg: 7.45, basis: 'perKgCreditedMetal', currency: 'EUR'},
        pt: {ratePerKg: 435,  basis: 'perKgCreditedMetal', currency: 'EUR'},
        pd: {ratePerKg: 360,  basis: 'perKgCreditedMetal', currency: 'EUR'},
      },
      impurityCharges: [
        {element: 'cd', basis: 'perKgElement', bands: [{maxPct: 0.5, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 30}]},
        {element: 'ni', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 7.5}]},
        {element: 'sn', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 5}]},
        {element: 'zn', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 10}]},
        {element: 'bi', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 20}]},
        {element: 'pb', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 10}]},
        {element: 'co', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 7.5}]},
        {element: 'cr', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 7.5}]},
        {element: 'sb', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 20}]},
        {element: 'halides', basis: 'perKgElement', bands: [{maxPct: 1, state: 'free'}, {maxPct: null, state: 'charge', ratePerKg: 15}]},
      ],
      lotAnalysisFees: [
        {elementCountMin: 1, elementCountMax: 2, amount: 200, currency: 'EUR'},
        {elementCountMin: 3, elementCountMax: 3, amount: 250, currency: 'EUR'},
        {elementCountMin: 4, elementCountMax: 4, amount: 300, currency: 'EUR'},
      ],
      preparationCost: {ratePerKg: 1.70, minimumPerLot: 170, currency: 'EUR'},
      treatmentCosts: [
        {minPurityPct: 90, maxPurityPct: null, ratePerKg: 0.45, currency: 'EUR'},
        {minPurityPct: 80, maxPurityPct: 90, ratePerKg: 1.55, currency: 'EUR'},
        {minPurityPct: 70, maxPurityPct: 80, ratePerKg: 2.75, currency: 'EUR'},
        {minPurityPct: 60, maxPurityPct: 70, ratePerKg: 3.90, currency: 'EUR'},
        {minPurityPct: 50, maxPurityPct: 60, ratePerKg: 5.05, currency: 'EUR'},
        {minPurityPct: 40, maxPurityPct: 50, ratePerKg: 6.20, currency: 'EUR'},
        {minPurityPct: 0, maxPurityPct: 40, ratePerKg: 7.35, currency: 'EUR'},
      ],
      metalTradingFeesPerTozUsd: {au: 1.50, ag: 0.15, pt: 3.50, pd: 3.50},
      metalThresholds: {
        au: {minimumDeductionPct: 0.003},
        pt: {minimumDeductionPct: 0.003},
        pd: {minimumDeductionPct: 0.003},
      },
      additionalNotes:
        'SLA anchor "after written release" is modeled with the existing `received` milestone ' +
        '(no separate release milestone in the current schema).',
    },
    slaModifiers: [
      {
        when: {metal: 'ag', purityPctMax: 70},
        addBusinessDays: 10,
        notes: 'Ag < 70% triggers an extra 10 bd on top of the per-tier SLA (matches Saxonia term sheet).',
      },
    ],
    assayFee: {
      au: {feeUsd: 0},
      ag: {feeUsd: 0},
      pt: {feeUsd: 0},
      pd: {feeUsd: 0},
    },
    otherFees: [
      // Saxonia bundles treatment / wire / copper-addition into the EUR refining schedule; empty array intentional.
    ],
    standardFees: {},
    expectedMilestones: [
      {kind: 'shipped', label: 'Shipped'},
      {kind: 'received', label: 'Acknowledged Receipt (written-release SLA anchor)'},
      {kind: 'melted', label: 'Melt Date'},
      {kind: 'assayed', label: 'Assay Result Provided'},
      {kind: 'settled', label: 'Settlement'},
    ],
  },
  // Synthetic multi-metal demo contract — NOT a real partner term sheet.
  // Keeps PDF-faithful seeds (Asahi/Metalor/Saxonia) untouched while exercising
  // upstream >4 paid metals (classics + cu) through settlement + switch-preview.
  {
    id: 'contract-demo-multi-metal-2026',
    refiningPartnerId: 'partner-asahi',
    title: 'Demo — multi-metal Au+PGM+Cu',
    effectiveFrom: '2026-01-01',
    effectiveTo: '2099-12-31',
    currency: 'USD',
    materialAcceptanceCriteria:
      'SYNTHETIC DEMO CONTRACT — not a live quote. Au scrap with PGM + payable Cu ' +
      'so the prototype can exercise >4 paid metals without rewriting real PDF seeds.',
    tierDimension: 'purityPct',
    tierQualifyingMetal: 'au',
    paidMetals: ['au', 'ag', 'pt', 'pd', 'cu'],
    primaryMetals: ['au'],
    lotWeightRequirements: [],
    settlementTerms:
      'Demo-only. Mirrors Asahi-shaped Au qualifier + net-wt refining on Ag/Pt/Pd/Cu ' +
      'for settlement-preview honesty. Do not treat as commercial terms.',
    metalRates: [
      {metalType: 'au', tierMin: 99.5, tierMax: null, accountabilityPercent: 100.00, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 10, notes: null},
      {metalType: 'au', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 99.80, fee: 0, feeWaived: true, feeBasis: 'creditedFineToz', settlementBusinessDays: 10, notes: null},
      {metalType: 'ag', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 95.00, fee: 0.25, feeBasis: 'netWeightReceivedToz', settlementBusinessDays: 10, notes: null},
      {metalType: 'pt', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 90.00, fee: 25.00, feeBasis: 'netWeightReceivedToz', settlementBusinessDays: 60, notes: null},
      {metalType: 'pd', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 90.00, fee: 25.00, feeBasis: 'netWeightReceivedToz', settlementBusinessDays: 60, notes: null},
      {metalType: 'cu', tierMin: 50.0, tierMax: 99.5, accountabilityPercent: 90.00, fee: 0.10, feeBasis: 'netWeightReceivedToz', settlementBusinessDays: 10, notes: 'Demo Cu paid metal'},
    ],
    referenceFeeSchedule: {
      perLotMinimumFee: 400,
      metalThresholds: {
        ag: {minimumPayablePct: 0.50, minimumDeductionPct: 0.20},
        pt: {minimumPayablePct: 0.05, minimumDeductionPct: 0.03},
        pd: {minimumPayablePct: 0.05, minimumDeductionPct: 0.03},
      },
      additionalNotes: 'Synthetic multi-metal demo — see contract title.',
    },
    slaModifiers: null,
    assayFee: {au: {feeUsd: 100}},
    otherFees: [
      {code: 'treatment', kind: 'perToz', perTozUsd: 0.70, notes: null},
      {code: 'wire', kind: 'flat', flatUsd: 25, notes: null},
    ],
    standardFees: {},
    expectedMilestones: [
      {kind: 'shipped', label: 'Shipped'},
      {kind: 'received', label: 'Acknowledged Receipt'},
      {kind: 'melted', label: 'Melt Date'},
      {kind: 'pin_returned', label: 'Customer Pin Returned'},
      {kind: 'assayed', label: 'Assay Result Provided'},
      {kind: 'settled', label: 'Settlement'},
    ],
  },
]

// Module-local sequence counter that yields `ML-2026-0001`, `ML-2026-0002`,
// … each call. Vuex normally derives codes from persisted state via
// `computeNextUserLotCode`; this fallback is only used when a mock-api call
// is exercised without that context (e.g. direct unit tests).
export const generateMockMasterLotCode = (() => {
  let nextSequenceNumber = 1
  return () => `ML-2026-${String(nextSequenceNumber++).padStart(4, '0')}`
})()
